import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';
import { isAfter, parseISO, startOfWeek } from 'date-fns';
import {
  type GetPendingTimeSlotsArgs,
  type GetPendingTimeSlotsRejectedResponse,
  type GetPendingTimeSlotsResponse,
  type GetTimeSlotsArgs,
  type GetTimeSlotsPayload,
  type LoadTimeSlotsDataArgs,
  type LoadTimeSlotsDataResult,
  type TimeSlotsState,
} from './timeSlotsTypes';
import {
  type AvailableTimeslotsResponse,
  NO_STORE_ID,
  type Slot,
} from '../../services/appointment/model';
import {
  getPostalCodeForLocation,
  isStoreService,
  isTimeInBrowserSettings,
  type ServiceLocation,
  startOfWeekDayToNumber,
} from '../..//helpers/appHelper';
import services from '../../services';
import { getInitialSessionOnClientEntry } from '../../helpers/sessionHelper';
import { isFeatureEnabled } from '../../helpers/featureHelper';
import {
  markSelectedSlotAsCompleted,
  toSlots,
} from '../../services/appointment/utils';
import { type RootState } from '../../store/store';
import { updateSelectedReplacementServiceStore } from '../app/appReducer';
import { getStores } from '../stores/storesReducer';

const MAX_TIME_SLOTS_IN_RESPONSE = CSS_CONFIG.services.maxTimeSlotsInResponse;
const MAX_TIME_SLOT_COUNT_TO_SHOW_REPLACEMENT = 5;
const WEEK_PERIOD = 7;

const initialState = (): TimeSlotsState => {
  const restoredState = getInitialSessionOnClientEntry();
  return {
    from: restoredState.from || new Date().toISOString(),
    numberOfWeeksBack: restoredState.numberOfWeeksBack || [],
    prevEnabled: false,
    availableTimeSlots: {},
    replacementServiceAvailableTimeSlots: {},
    isLoading: false,
    showRetry: false,
    pendingSlots: {
      isUpdating: false,
      activeUpdatingCount: 0,
    },
  };
};

async function fetchAndAggregateTimeSlots(
  zipCode: string,
  serviceId: string,
  location: ServiceLocation,
  storeId: string,
  from: string,
  serviceCode: string,
  weekStartsOn: 0 | 1,
  language?: string,
): Promise<{ allTimeSlots: AvailableTimeslotsResponse[]; error: boolean }> {
  const requests = Array.from({ length: 4 }, (_, i) => {
    const weekStart = startOfWeek(parseISO(from), { weekStartsOn });
    weekStart.setDate(weekStart.getDate() + i * 7);
    return services.appointment.getAvailableTimeslots(
      zipCode,
      serviceId,
      location,
      storeId,
      weekStart.toISOString(),
      serviceCode,
      WEEK_PERIOD,
      language,
    );
  });

  const results = await Promise.all(requests);

  const allTimeSlots: AvailableTimeslotsResponse[] = [];
  let error = false;
  for (const res of results) {
    if (res.error) {
      error = true;
    }
    if (res.result && Array.isArray(res.result)) {
      allTimeSlots.push(...res.result);
    }
  }

  return { allTimeSlots, error };
}

export const getPendingTimeSlots = createAsyncThunk<
  GetPendingTimeSlotsResponse,
  GetPendingTimeSlotsArgs,
  { rejectValue: GetPendingTimeSlotsRejectedResponse }
>(
  'timeSlots/getPendingTimeSlots',
  async (
    {
      zipCode,
      serviceId,
      location,
      storeId,
      from,
      serviceCode,
      isReplacementService,
      numberOfDays,
      pendingSelectedSlot,
      language,
    },
    { rejectWithValue },
  ) => {
    const fromDate = parseISO(from).toISOString();

    if (!location) {
      return rejectWithValue({
        storeId,
        from: fromDate,
        isReplacementService,
      });
    }

    const { result, error } = await services.appointment.getAvailableTimeslots(
      zipCode,
      serviceId,
      location,
      storeId,
      fromDate,
      serviceCode,
      numberOfDays,
      language,
    );
    if (error) {
      return rejectWithValue({
        storeId,
        from: fromDate,
        isReplacementService,
      });
    }

    const slots: Slot[] = toSlots(
      result,
      isTimeInBrowserSettings(location, CSS_CONFIG),
      fromDate,
      [],
      result.length === MAX_TIME_SLOTS_IN_RESPONSE,
      CSS_CONFIG,
    );

    markSelectedSlotAsCompleted(slots, pendingSelectedSlot);

    return {
      storeId,
      slots,
      isReplacementService,
    };
  },
);

export const getTimeSlots = createAsyncThunk<
  GetTimeSlotsPayload,
  GetTimeSlotsArgs
>(
  'timeSlots/getTimeSlots',
  async ({
    zipCode,
    serviceId,
    location,
    storeId,
    from,
    serviceCode,
    isReplacementService,
    zipCodeForLocation,
    isExternalService,
    language,
  }) => {
    const weekStartsOn = startOfWeekDayToNumber(
      CSS_CONFIG.formats.firstDayOfWeek,
    );
    const firstDayOfWeek = startOfWeek(parseISO(from), { weekStartsOn });
    const firstDayOfWeekISOstring = firstDayOfWeek.toISOString();
    const prevEnabled = isAfter(firstDayOfWeek, new Date());
    let availableTimeSlots: AvailableTimeslotsResponse[] = [];
    const { splitTimeSlotRequest } = CSS_CONFIG.services;

    if (splitTimeSlotRequest.includes(serviceId)) {
      const { allTimeSlots, error } = await fetchAndAggregateTimeSlots(
        zipCode,
        serviceId,
        location,
        storeId,
        from,
        serviceCode,
        weekStartsOn,
        language,
      );
      availableTimeSlots = allTimeSlots;
      if (error) {
        return Promise.reject();
      }
    } else {
      const { result, error } =
        await services.appointment.getAvailableTimeslots(
          zipCode,
          serviceId,
          location,
          storeId,
          firstDayOfWeekISOstring,
          serviceCode,
          undefined,
          language,
        );
      availableTimeSlots = result;
      if (error) {
        return Promise.reject();
      }
    }

    const totalTimeSlots = availableTimeSlots.length;
    if (totalTimeSlots === 0) {
      return {
        storeId,
        from: firstDayOfWeekISOstring,
        prevEnabled,
        slots: [],
        isReplacementService,
      };
    }

    const capacity =
      !isExternalService &&
      isFeatureEnabled('capacityEndpoint') &&
      totalTimeSlots === MAX_TIME_SLOTS_IN_RESPONSE
        ? await services.appointment.getTimeSlotCapacity(
            zipCodeForLocation,
            serviceId,
            firstDayOfWeekISOstring,
            isTimeInBrowserSettings(location, CSS_CONFIG),
            language,
          )
        : [];

    const slots: Slot[] = toSlots(
      availableTimeSlots,
      isTimeInBrowserSettings(location, CSS_CONFIG),
      firstDayOfWeekISOstring,
      capacity,
      !!capacity.length,
      CSS_CONFIG,
      isReplacementService,
    );

    return {
      storeId,
      from: firstDayOfWeekISOstring,
      prevEnabled,
      slots,
      isReplacementService,
    };
  },
);

/**
 * Orchestrating thunk that fetches original service timeslots and conditionally
 * fetches replacement service timeslots (including replacement stores if needed).
 *
 * Replacement timeslots are fetched when:
 * - The original service has a replacement service configured AND
 * - replacementServiceAlwaysShow is true OR original timeslots count < 5
 */
export const loadTimeSlotsData = createAsyncThunk<
  LoadTimeSlotsDataResult,
  LoadTimeSlotsDataArgs,
  { state: RootState }
>(
  'timeSlots/loadTimeSlotsData',
  async (
    {
      from: fromOverride,
      storeId: storeIdOverride,
      language: languageOverride,
    },
    { dispatch, getState },
  ) => {
    // Timeslots are cleared in the loadTimeSlotsData.pending extra reducer
    const rootState = getState();

    const { serviceId, storeId, language } = rootState.app.selected;
    const zipCode = rootState.search.postCode;
    const from = fromOverride || rootState.timeSlots.from;
    const stores = rootState.stores.availableStores;
    const service = rootState.services.availableServices.find(
      (s) => s.serviceId === serviceId,
    );

    if (!service) {
      return Promise.reject(new Error('No service selected'));
    }

    // 1. Fetch original service timeslots
    const originalResult = await dispatch(
      getTimeSlots({
        zipCode,
        serviceId,
        location: service.location,
        storeId: storeIdOverride || storeId,
        from,
        serviceCode: service.serviceCode,
        isReplacementService: false,
        zipCodeForLocation: getPostalCodeForLocation(
          storeIdOverride || storeId,
          stores,
          CSS_CONFIG,
          service.location,
        ),
        isExternalService: service.isExternalService,
        language: languageOverride || language || undefined,
      }),
    ).unwrap();

    // 2. Check if replacement service timeslots should be fetched
    const totalOriginalTimeSlots = originalResult.slots.reduce(
      (acc, slot) => acc + slot.timeSlots.length,
      0,
    );

    let hasReplacementServiceTimeSlots = false;
    const { replacementService } = service;
    if (
      replacementService &&
      replacementService.serviceId &&
      replacementService.serviceCode &&
      replacementService.location &&
      (CSS_CONFIG.services.replacementServiceAlwaysShow ||
        totalOriginalTimeSlots < MAX_TIME_SLOT_COUNT_TO_SHOW_REPLACEMENT)
    ) {
      let replacementStores = rootState.stores.replacementServiceStores;
      let replacementStoreId: string | undefined;

      // 3. Fetch replacement stores if needed (only for store services)
      if (isStoreService(replacementService.location)) {
        if (!replacementStores.length) {
          const storesResult = await dispatch(
            getStores({
              serviceProductId: replacementService.serviceId,
              replacementServiceLocation: replacementService.location,
              serviceLocation: service.location,
            }),
          ).unwrap();
          replacementStores = storesResult.stores;
          replacementStoreId = storesResult.selectedStoreId;
        } else {
          replacementStoreId =
            rootState.app.selected.replacementService.storeId;
        }
      } else {
        // Non-store replacement service (e.g. remote) uses default store ID
        replacementStoreId = NO_STORE_ID;
      }

      // 4. Fetch replacement service timeslots
      if (replacementStoreId) {
        const replacementStore =
          replacementStores.find((s) => s.value === replacementStoreId) || null;

        // Update app selected state for non-store services (getStores handles store services)
        if (!isStoreService(replacementService.location)) {
          dispatch(
            updateSelectedReplacementServiceStore({
              store: replacementStore,
              storeId: replacementStoreId,
            }),
          );
        }

        const replacementResult = await dispatch(
          getTimeSlots({
            zipCode,
            serviceId: replacementService.serviceId,
            location: replacementService.location,
            storeId: replacementStoreId,
            from,
            serviceCode: replacementService.serviceCode,
            isReplacementService: true,
            zipCodeForLocation: getPostalCodeForLocation(
              replacementStoreId,
              replacementStores,
              CSS_CONFIG,
              replacementService.location,
            ),
            isExternalService: replacementService.isExternalService,
            language: languageOverride || language || undefined,
          }),
        ).unwrap();

        hasReplacementServiceTimeSlots = replacementResult.slots.some(
          (slot) => slot.timeSlots.length > 0,
        );
      }
    }

    return {
      hasOriginalTimeSlots: originalResult.slots.some(
        (slot) => slot.timeSlots.length > 0,
      ),
      hasReplacementServiceTimeSlots,
    };
  },
);

const timeSlotsSlice = createSlice({
  name: 'timeSlots',
  initialState: initialState(),
  reducers: {
    clearAvailableTimeSlots(state) {
      state.availableTimeSlots = {};
      state.replacementServiceAvailableTimeSlots = {};
    },
    resetTimeSlotsFromDate(state) {
      state.from = new Date().toISOString();
      state.numberOfWeeksBack = [];
    },
    updateNumberOfWeeksBack(state, action: PayloadAction<number | undefined>) {
      if (action.payload !== undefined) {
        state.numberOfWeeksBack.push(action.payload);
      } else {
        state.numberOfWeeksBack.pop();
      }
    },
    clearSlotErrors(state) {
      Object.values(state.availableTimeSlots).forEach((slotsObject) => {
        slotsObject.forEach((slot) => {
          if (slot.status === 'error') {
            slot.status = 'pending';
          }
        });
      });
      Object.values(state.replacementServiceAvailableTimeSlots).forEach(
        (slotsObject) => {
          slotsObject.forEach((slot) => {
            if (slot.status === 'error') {
              slot.status = 'pending';
            }
          });
        },
      );
    },
    resetTimeSlotsSessionState(state) {
      state.from = new Date().toISOString();
      state.numberOfWeeksBack = [];
      state.availableTimeSlots = {};
      state.replacementServiceAvailableTimeSlots = {};
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      getTimeSlots.fulfilled,
      (state, action: PayloadAction<GetTimeSlotsPayload>) => {
        state.from = action.payload.from;
        state.prevEnabled = action.payload.prevEnabled;
        if (action.payload.isReplacementService) {
          state.replacementServiceAvailableTimeSlots = {
            [action.payload.storeId]: action.payload.slots,
          };
        } else {
          state.availableTimeSlots = {
            [action.payload.storeId]: action.payload.slots,
          };
        }
      },
    );
    builder.addCase(loadTimeSlotsData.pending, (state, action) => {
      if (action.meta.arg.from) {
        state.from = action.meta.arg.from;
      }
      state.isLoading = true;
      state.showRetry = false;
      state.availableTimeSlots = {};
      state.replacementServiceAvailableTimeSlots = {};
    });
    builder.addCase(loadTimeSlotsData.fulfilled, (state) => {
      state.isLoading = false;
      state.showRetry = false;
    });
    builder.addCase(loadTimeSlotsData.rejected, (state) => {
      state.isLoading = false;
      state.showRetry = true;
    });
    builder.addCase(
      getPendingTimeSlots.fulfilled,
      (
        state,
        action: PayloadAction<{
          storeId: string;
          slots: Slot[];
          isReplacementService?: boolean;
        }>,
      ) => {
        const activeUpdatingCount = state.pendingSlots.activeUpdatingCount - 1;
        state.pendingSlots = {
          isUpdating: activeUpdatingCount > 0,
          activeUpdatingCount,
        };
        if (action.payload.isReplacementService) {
          state.replacementServiceAvailableTimeSlots = {
            ...state.replacementServiceAvailableTimeSlots,
            [action.payload.storeId]:
              state.replacementServiceAvailableTimeSlots[
                action.payload.storeId
              ].map((existingSlot) => {
                const updatedSlot = action.payload.slots.find(
                  (slot) => slot.date === existingSlot.date,
                );
                return updatedSlot || existingSlot;
              }),
          };
        } else {
          state.availableTimeSlots = {
            ...state.availableTimeSlots,
            [action.payload.storeId]: state.availableTimeSlots[
              action.payload.storeId
            ].map((existingSlot) => {
              const updatedSlot = action.payload.slots.find(
                (slot) => slot.date === existingSlot.date,
              );
              return updatedSlot || existingSlot;
            }),
          };
        }
      },
    );
    builder.addCase(getPendingTimeSlots.rejected, (state, action) => {
      const activeUpdatingCount = state.pendingSlots.activeUpdatingCount - 1;
      state.pendingSlots = {
        isUpdating: activeUpdatingCount > 0,
        activeUpdatingCount,
      };

      if (!action.payload) return;
      const { storeId, from, isReplacementService } = action.payload;

      if (isReplacementService) {
        const slotFound = state.replacementServiceAvailableTimeSlots[
          storeId
        ].find((slot) => slot.date === from);
        if (slotFound) {
          slotFound.status = 'error';
        }
      } else {
        const slotFound = state.availableTimeSlots[storeId].find(
          (slot) => slot.date === from,
        );
        if (slotFound) {
          slotFound.status = 'error';
        }
      }
    });
    builder.addCase(getPendingTimeSlots.pending, (state) => {
      state.pendingSlots = {
        isUpdating: true,
        activeUpdatingCount: state.pendingSlots.activeUpdatingCount + 1,
      };
    });
  },
});

export const {
  clearAvailableTimeSlots,
  resetTimeSlotsFromDate,
  updateNumberOfWeeksBack,
  clearSlotErrors,
  resetTimeSlotsSessionState,
} = timeSlotsSlice.actions;

export default timeSlotsSlice.reducer;
